import React, { useCallback, useRef, useEffect, useMemo, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import MenuItem from "@mui/material/MenuItem";
import { ResponsivePie } from "@nivo/pie";
import { Link } from "react-router-dom";
import Menu from "@mui/material/Menu";

import COMMON from "common";
import api from "services/api";
import pathnames from "routes/pathnames";
import classNames from "common/class-names";
import { isDateBeforeToday, isSameDate, formatDatePattern, getDaysDifference } from "common/calendar";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppClockIcon from "components/icons/app-clock-icon";
import AppReceiptIcon from "components/icons/app-receipt-icon";
import AppChevronIcon from "components/icons/app-chevron-icon";
import AppCalendarCheckIcon from "components/icons/app-calendar-check-icon";
import AppAnnouncementIcon from "components/icons/app-announcement-icon";
import AppCreateAnnouncementModal from "components/app-create-announcement-modal";
import AppAnnouncementDescriptionModal from "components/pages/dashboard/app-announcement-description-modal";
import pdfIcon from "assets/images/pdf-icon.svg";
import pinIcon from "assets/images/pin-icon.svg";
import moreIcon from "assets/images/more-icon.svg";
import editIcon from "assets/images/edit-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import pinnnedIcon from "assets/images/pinned-icon.svg";
import holidayIcon from "assets/images/holiday-icon.svg";
import AppEmptyState from "components/app-empty-state";

const formatAnnouncementDate = (date) => {
	if (!date) return "-";

	const days = getDaysDifference(date, new Date());

	if (days === 0) return "Today";

	if (days === 1) return "1 day ago";

	if (days > 1 && days <= 7) return days + " days ago";

	return formatDatePattern(date, "DD/MM/YYYY");
};

const isAnnouncementExpired = (announcement) => !isDateBeforeToday(new Date(), announcement.endDate);

const getAnnouncementRank = (announcement) => (announcement.pinned ? 0 : 2) + (isAnnouncementExpired(announcement) ? 1 : 0);

// Case Format for Legend
const formatLegendLabel = (label) => (label || "").replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

const LeaveTypeTag = ({ leaveType }) => {
	const cellRef = useRef();
	const measureRef = useRef();
	const [isShortForm, setIsShortForm] = useState(false);

	// Short Form for Leave Type
	const shortForm = useMemo(() => COMMON.BENEFITS.LEAVE_SHORT_FORMS[leaveType?.toUpperCase().replace(/\s+/g, "_")] || "", [leaveType]);

	//prettier-ignore
	const className = useMemo(() => classNames({ "leave-type": true, [`leave-type--${leaveType?.toLowerCase().replace(/\s+/g, "-")}`]: !!leaveType }), [leaveType]);

	useEffect(() => {
		const cell = cellRef.current;
		const measure = measureRef.current;

		if (!cell || !measure || !shortForm) return;

		const onHandleMeasure = () => setIsShortForm(measure.offsetWidth > cell.clientWidth);

		onHandleMeasure();

		const observer = new ResizeObserver(onHandleMeasure);

		observer.observe(cell);

		return () => observer.disconnect();
	}, [shortForm, leaveType]);

	if (!leaveType) return <p className="leaves__time-off">-</p>;

	return (
		<div className="leaves__type" ref={cellRef}>
			{/* Leave Type */}
			<div className={className} title={leaveType}>
				<p className="leave-type__text">{isShortForm ? shortForm : leaveType}</p>
			</div>

			{/* Leave Duration Type */}
			<span className="leave-type__measure" ref={measureRef} aria-hidden="true">
				{leaveType}
			</span>
		</div>
	);
};

// Total Employee Design for Pie Chart
const TotalEmployeeMetric = ({ centerX, centerY, dataWithArc }) => {
	const total = dataWithArc.reduce((t, o) => t + (o.value || 0), 0);

	if (!total) return null;

	return (
		<g>
			<text className="total-employee__metric-value" x={centerX} y={centerY - 8} textAnchor="middle" dominantBaseline="central">
				{total}
			</text>

			<text className="total-employee__metric-label" x={centerX} y={centerY + 12} textAnchor="middle" dominantBaseline="central">
				Employees
			</text>
		</g>
	);
};

const PageDashboard = (props) => {
	//prettier-ignore
	const tabsNav = useMemo(() => [
		{ label: "Posted", value: "Posted" },
		{ label: "Upcoming", value: "Upcoming" },
	], []);
	const dispatch = useDispatch();
	const totalEmloyeeRef = useRef();
	const createAnnouncementRef = useRef();
	const announcementDescriptionRef = useRef();
	const [data, setData] = useState({});
	const [tab, setTab] = useState(tabsNav[0].value);
	const [pendings, setPendings] = useState({ leaves: 0, claims: 0, overtimes: 0 });
	const [upcomings, setUpcomings] = useState({});
	const [announcements, setAnnouncements] = useState({});
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedAnnouncement, setSelectedAnnouncement] = useState({});
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const remainingAnnouncements = useMemo(() => {
		const max = 15;
		const totalUpcomings = upcomings.content?.length || 0;
		const totalAnnuncements = announcements.content?.length || 0;

		return max - totalUpcomings - totalAnnuncements;
	}, [announcements, upcomings]);
	const disabledCreateAnnouncement = useMemo(() => remainingAnnouncements === 0, [remainingAnnouncements]);
	const employeeSummary = useMemo(() => {
		const summaries = data.employeeSummary || [];
		const total = summaries.reduce((t, o) => t + (o.value || 0), 0);
		const legends = [...summaries]
			.sort((a, b) => b.value - a.value)
			.map((o) => {
				const percentage = total ? (o.value / total) * 100 : 0;

				return { ...o, label: formatLegendLabel(o.label), percentage: o.value && percentage < 1 ? "<1%" : Math.round(percentage) + "%" };
			});

		return { total, legends };
	}, [data.employeeSummary]);
	//prettier-ignore
	const quickActions = useMemo(() => [
		{ accent: "green", label: "Pending Leave Approval", count: pendings.leaves, pathname: pathnames.leaveManagement.leavesApproval, icon: <AppCalendarCheckIcon color="currentColor" /> },
		{ accent: "purple", label: "Pending Claims Approval", count: pendings.claims, pathname: pathnames.claimManagement.claimApproval, icon: <AppReceiptIcon color="currentColor" /> },
		{ accent: "orange", label: "Pending Overtime Approval", count: pendings.overtimes, pathname: pathnames.overtimeManagement.overtimeApproval, icon: <AppClockIcon color="currentColor" /> },
	], [pendings]);

	const formatDate = (date) => {
		const now = new Date().getDate();
		const day = new Date(date).getDate();

		if (now === day) return "Today";

		if (day > 3 && day < 21) return day + "th";

		switch (day % 10) {
			case 1:
				return day + "st";
			case 2:
				return day + "nd";
			case 3:
				return day + "rd";
			default:
				return day + "th";
		}
	};

	const formatHolidayDate = (holiday) => {
		if (!holiday.endDate || isSameDate(holiday.startDate, holiday.endDate)) return formatDate(holiday.startDate);

		return formatDate(holiday.startDate) + " - " + formatDate(holiday.endDate);
	};

	const onHandleInitialize = useCallback(async () => {
		let response = null;
		let responseAnnouncements = null;
		let responseUpcomings = null;
		let responsePendingLeaves = null;
		let responsePendingClaims = null;
		let responsePendingOvertimes = null;

		try {
			response = await api.get.dashboard.admin();
			responseAnnouncements = await api.get.announcements.list({ page: 0, size: 15, sort: "pinned,desc&sort=startDate,desc" });
			responseUpcomings = await api.get.announcements.upcoming({ page: 0, size: 15 });
			responsePendingLeaves = await api.get.leaves.pendingApprovalList({ page: 0, size: 1 });
			responsePendingClaims = await api.get.claims.pendingApprovalList({ page: 0, size: 1 });
			responsePendingOvertimes = await api.get.overtime.pending({ page: 0, size: 1 });
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData({ ...response, employeeSummary: response.employeeSummary.map((o, i) => ({ ...o, id: o.contractType, label: o.contractType, value: o.count })).filter((o) => o.label !== "Total") });
		}
		if (responseAnnouncements) setAnnouncements(responseAnnouncements);
		if (responseUpcomings) setUpcomings(responseUpcomings);
		if (responsePendingLeaves || responsePendingClaims || responsePendingOvertimes) setPendings((prev) => ({ ...prev, leaves: responsePendingLeaves?.totalElements || 0, claims: responsePendingClaims?.totalElements || 0, overtimes: responsePendingOvertimes?.totalElements || 0 }));
	}, []);

	//prettier-ignore
	const onHandleDeleteAnnouncement = useCallback(async (id) => {
		onhandleCloseMenu();

		let response = null;

		try {
			await api.post.announcements.delete(id);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			if (tab === tabsNav[1].value) setUpcomings((prev) => ({ ...prev, content: prev.content.filter((o) => o.id !== id) }));
			if (tab === tabsNav[0].value) setAnnouncements((prev) => ({ ...prev, content: prev.content.filter((o) => o.id !== id) }));
			dispatch(promptAlertMessage({ message: "Announcement has been deleted successfully" }));
		}
	}, [dispatch, tab, tabsNav]);

	//prettier-ignore
	const onHandleUpdateAnnouncementPin = useCallback(async (obj) => {
		let response = null;

		try {
			const payload = {
				"id": obj.id,
				"title": obj.title,
				"description": obj.description,
				"pinned": !obj.pinned,
				"startDate": obj.startDate,
				"endDate": obj.endDate
			}
			await api.post.announcements.update(payload);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			await onHandleInitialize();
			dispatch(promptAlertMessage({ message: "Announcement has been updated successfully" }));
		}
	}, [dispatch, onHandleInitialize]);

	const onHandleCreateAnnouncement = useCallback((args) => {
		onhandleCloseMenu();
		createAnnouncementRef.current.onHandleShow(args);
	}, []);

	const onHandleReadMoreDescription = useCallback((announcement) => {
		announcementDescriptionRef.current.onHandleShow({ title: announcement.title, description: announcement.description });
	}, []);

	const onHandleSelectAnnouncement = useCallback((event, original) => {
		setSelectedAnnouncement(original);
		setAnchorEl(event.currentTarget);
	}, []);

	const onhandleCloseMenu = () => {
		setAnchorEl(null);
	};

	//prettier-ignore
	const Tabs = useCallback((obj) => {
		let list = [];

		switch (obj.tab) {
			case "Posted":
				if (obj.announcements && obj.announcements.length > 0) {
					list = obj.announcements;
				}
				break;
			case "Upcoming":
				if (obj.upcomings && obj.upcomings.length > 0) {
					list = obj.upcomings;
				}
				break;
			default:
				list = [];
				break;
		}

		/* Pinned announcements float to the top and expired ones sink to the bottom, keeping the start date order from the api within each group */
		list = [...list].sort((a, b) => getAnnouncementRank(a) - getAnnouncementRank(b));

		return (
			<ul className="announcements announcements--scrollable">
				{list.length > 0 ? (
					list.map((o, i) => {
						const pinClassName = classNames({
							announcements__button: true,
							"announcements__button--pin": !o.pinned,
							"announcements__button--pinned": o.pinned,
						});

						const expired = isAnnouncementExpired(o);
						const itemClassName = classNames({ announcements__item: true, "announcements__item--expired": expired });

						return (
							<li key={o.id} className={itemClassName} role="button" tabIndex={0} onClick={() => obj.onHandleReadMoreDescription(o)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); obj.onHandleReadMoreDescription(o); } }}>
								<div className="announcements__content">
									<div className="announcements__heading">
										<span className="announcements__dot" />
										<p className="announcements__title">{o.title}</p>
										{expired && <span className="announcements__tag">Expired</span>}
									</div>

									<p className="announcements__meta">
										<span className="announcements__date">{formatAnnouncementDate(o.startDate)}</span>
										<span className="announcements__separator" aria-hidden="true">·</span>
										<span className="announcements__summary">{o.description}</span>
									</p>
								</div>

								<div className="announcements__actions" onClick={(event) => event.stopPropagation()}>
									{o.hrAnnouncementFile && (
										<a className="announcements__button announcements__button--pdf" href={o.hrAnnouncementFile.filePath} target="_blank" rel="noreferrer">
											<img src={pdfIcon} alt="attachement" />
										</a>
									)}
									<button type="button" className={pinClassName} onClick={() => obj.onHandleUpdateAnnouncementPin(o)}>
										<img src={o.pinned ? pinnnedIcon : pinIcon} alt="pin" />
									</button>
									<button type="button" className="announcements__button announcements__button--more" onClick={(event) => obj.onHandleSelectAnnouncement(event, o)}>
										<img src={moreIcon} alt="more" />
									</button>
								</div>
							</li>
						);
					})
				) : (
					<AppEmptyState
						title="No records found"
						description="You don’t have any records yet"
						disabledButton
					/>
				)}
			</ul>
		);
	}, []);

	useEffect(() => {
		onHandleInitialize();
	}, [onHandleInitialize]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.DASHBOARD.ADMIN);
			cancelRequest(COMMON.ENDPOINT_PATH.ANNOUNCEMENTS.LIST);
			cancelRequest(COMMON.ENDPOINT_PATH.ANNOUNCEMENTS.UPCOMING);
			cancelRequest(COMMON.ENDPOINT_PATH.LEAVES.PENDING_APPROVAL_LIST);
			cancelRequest(COMMON.ENDPOINT_PATH.CLAIMS.PENDING_APPROVAL_LIST);
			cancelRequest(COMMON.ENDPOINT_PATH.OVERTIME.PENDING);
		};
	}, [cancelRequest]);

	return (
		<div className="page-dashboard">
			<div className="dashboard">
				<div className="dashboard__section dashboard__sectionTop">
					<div className="dashboard__column">
						<div className="card card--announcements-banner">
							<div className="announcements-banner">
								<div className="announcements-banner__icon">
									<AppAnnouncementIcon />
								</div>

								<div className="announcements-banner__content">
									<p className="announcements-banner__title">Announcements</p>
									<p className="announcements-banner__description">Stay updated with the latest company news.</p>
								</div>

								{/* prettier-ignore */}
								<div className="announcements-banner__actions">
									<p className="announcements-banner__remaining" title="You are allowed to create a maximum of 15 posts, including both posted and upcoming announcements.">
										Remaining <span>{remainingAnnouncements}</span>
									</p>
									<AppButton type="button" label="Create Announcement" icon={<span className="announcements-banner__plus">+</span>} onClick={onHandleCreateAnnouncement} disabled={disabledCreateAnnouncement} />
								</div>
							</div>
						</div>

						<div className="announcements-container card">
							{/* prettier-ignore */}
							<div className="card__header announcements__header">
								<p className="card__title">Latest Announcements</p>

								<div className="announcements__segments" role="tablist">
									{tabsNav.map((o) => {
										const segmentClassName = classNames({ announcements__segment: true, "announcements__segment--active": tab === o.value });

										return (
											<button key={o.value} type="button" role="tab" aria-selected={tab === o.value} className={segmentClassName} onClick={() => setTab(o.value)}>{o.label}</button>
										);
									})}
								</div>
							</div>

							{/* prettier-ignore */}
							<Tabs tab={tab} announcements={announcements.content} upcomings={upcomings.content} onHandleReadMoreDescription={onHandleReadMoreDescription} onHandleDeleteAnnouncement={onHandleDeleteAnnouncement} onHandleUpdateAnnouncementPin={onHandleUpdateAnnouncementPin} onHandleSelectAnnouncement={onHandleSelectAnnouncement} />
						</div>
					</div>

					<div className="dashboard__column dashboard__column--aside">
						<div className="card card--holiday-this-month">
							<div className="card__header">
								<p className="card__title">Holiday this Month</p>

								<Link className="card__link" to={pathnames.leaveManagement.holidayCalendars}>
									<div className="dashboard__icon">
										<p className="dashboard__label">View All</p>
										<AppChevronIcon color="#0245a9" />
									</div>
								</Link>
							</div>

							<ul className="celebrations">
								{
									data.holidays && data.holidays.length > 0 ? (
										data.holidays?.map((o, i) => {
											return (
												<li key={i} className="celebrations__item">
													<img className="celebrations__icon" src={holidayIcon} alt="holiday" />
													<div className="celebrations__content">
														<p className="celebrations__name">{o.title}</p>
													</div>
													<p className="celebrations__date">{formatHolidayDate(o)}</p>
												</li>
											);
										})
									)
									:
									(
										<AppEmptyState
											description="No holiday this month"
											disabledButton
											disabledImage
										/>
									)
								}
							</ul>
						</div>

						<div className="card card--total-employee">
							<div className="card__header">
								<p className="card__title">Total Employee</p>
								<p className="card__total">{employeeSummary.total || ""}</p>
							</div>

							<div className="total-employee" ref={totalEmloyeeRef}>
								<ul className="total-employee__legends">
									{employeeSummary.legends.map((o) => {
										return (
											<li className="total-employee__legend" key={o.id}>
												<span className="total-employee__badge" style={{ backgroundColor: o.color }} />
												<span className="total-employee__label">{o.label}</span>
												<span className="total-employee__value">{o.value}</span>
												<span className="total-employee__percentage">({o.percentage})</span>
											</li>
										);
									})}
								</ul>

								<div className="total-employee__chart">
									<ResponsivePie
										data={employeeSummary.legends}
										padAngle={0.7}
										cornerRadius={3}
										innerRadius={0.6}
										activeOuterRadiusOffset={8}
										layers={["arcs", TotalEmployeeMetric]}
										colors={{ datum: "data.color" }}
										margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="dashboard__section dashboard__sectionBottom">
					<div className="card card--celebration-corner">
						<div className="card__header">
							<p className="card__title">Celebration Corner</p>
						</div>

						<ul className="celebrations">
							{
								data.birthdayEmployees && data.birthdayEmployees.length > 0? (
									data.birthdayEmployees?.map((o, i) => {
										return (
											<li key={i} className="celebrations__item celebrations__item--celebration">
												<div className="celebrations__profile" style={{ backgroundImage: `url(${o.avatar})` }} />
												<p className="celebrations__name" title={o.name}>{o.name}</p>
												<p className="celebrations__date">{formatDate(o.date)}</p>
											</li>
										);
									})
								)
								:
								(
									<AppEmptyState
										description="No celebrations this month"
										disabledButton
										disabledImage
									/>	
								)
							}
						</ul>
					</div>

					<div className="card card--employee-on-leave">
						<div className="card__header">
							<p className="card__title">Employee on Leave</p>

							<Link className="card__link" to={pathnames.leaveManagement.leaves}>
								<div className="dashboard__icon">
									<p className="dashboard__label">View All</p>
									<AppChevronIcon color="#0245a9" />
								</div>
							</Link>
						</div>

						<ul className="leaves">
							{
								data.employeesOnLeave && data.employeesOnLeave.length > 0? (
									data.employeesOnLeave?.map((o, i) => {
										return (
											<li key={i} className="leaves__item">
												<div className="leaves__profile" style={{ backgroundImage: `url(${o.avatar})` }} />
												<p className="leaves__name" title={o.name}>{o.name}</p>

												<LeaveTypeTag leaveType={o.leaveType} />

												<p className="leaves__time-off">{o.timeOffType || "-"}</p>
											</li>
										);
									})
								)
								:
								(
									<AppEmptyState
										description="No employee on leave today"
										disabledButton
										disabledImage
									/>	
								)
							}
						</ul>
					</div>

					<div className="card card--quick-actions">
						<div className="card__header">
							<p className="card__title">Quick Actions</p>
						</div>

						<ul className="quick-actions">
							{quickActions.map((o) => {
								const itemClassName = classNames({ "quick-actions__item": true, ["quick-actions__item--" + o.accent]: true });

								const content = (
									<Fragment>
										<div className="quick-actions__icon">
											{o.icon}
											{o.count > 0 && <span className="quick-actions__badge">{o.count > 9 ? "9+" : o.count}</span>}
										</div>

										<p className="quick-actions__label">{o.label}</p>
									</Fragment>
								);

								return (
									<li key={o.label} className={itemClassName}>
										{/* prettier-ignore */}
										{o.pathname ? (
											<Link className="quick-actions__action" to={o.pathname} aria-label={o.label + ", " + o.count + " pending"}>{content}</Link>
										) : (
											<div className="quick-actions__action quick-actions__action--disabled">{content}</div>
										)}
									</li>
								);
							})}
						</ul>
					</div>
				</div>
			</div>

			<AppCreateAnnouncementModal ref={createAnnouncementRef} onHandleGetList={onHandleInitialize} />

			<AppAnnouncementDescriptionModal ref={announcementDescriptionRef} />

			{/* prettier-ignore */}
			<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onhandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
				<MenuItem onClick={() => onHandleCreateAnnouncement(selectedAnnouncement)}><img className="app-table-menu__icon" src={editIcon} alt="edit" />Edit</MenuItem>
				<MenuItem className="app-table-menu__remove" onClick={() => onHandleDeleteAnnouncement(selectedAnnouncement.id)}><img className="app-table-menu__icon" src={removeIcon} alt="delete" />Delete</MenuItem>
			</Menu>
		</div>
	);
};

export default PageDashboard;
